import { calculateAge, zipCodeToState } from '@shared';
import {
  carescoutAvailableStates,
  manhattanLifeOmniFlexStcStates,
  MANHATTAN_LIFE_STC_MAX_ISSUE_AGE,
  MANHATTAN_LIFE_STC_MIN_ISSUE_AGE,
} from '@shared/carrierStateAvailability';

export const MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY = 40;
export const MAX_ELIGIBLE_AGE_CARESCOUT = 65;

export const TRADITIONAL_LTC_PAYMENT_OPTIONS = ['Monthly', 'Annually'];

/**
 * Selects the dedicated ManhattanLife short-term care view. Not a billing
 * frequency — the product is monthly-only — so it never participates in the
 * life or traditional-LTC quote pipelines.
 */
export const SHORT_TERM_CARE_PAYMENT_OPTION = 'Short-Term Care';

/**
 * ManhattanLife OmniFlex STC is billed monthly only, so it makes the Monthly
 * payment option relevant on its own — separately from CareScout, which is the
 * only other product using Monthly and which sells to a different set of states
 * and a lower age ceiling. Gating the two together hid the product entirely for
 * clients eligible for it but not for CareScout.
 *
 * State/age availability rather than "are there rows": the payment option has to
 * render before any quote fetch can happen.
 */
export function getShowShortTermCareOptions(
  quoteApplyManhattanLifeFlag: boolean | undefined,
  clientZipCode: string,
  clientBirthDate: string | Date,
) {
  if (!quoteApplyManhattanLifeFlag) return false;
  const state = zipCodeToState(clientZipCode);
  const clientAge = calculateAge(new Date(clientBirthDate));
  return (
    manhattanLifeOmniFlexStcStates.includes(state.stateAbbreviation as never) &&
    clientAge >= MANHATTAN_LIFE_STC_MIN_ISSUE_AGE &&
    clientAge <= MANHATTAN_LIFE_STC_MAX_ISSUE_AGE
  );
}

export function getShowTraditionalLTCOptions(
  clientZipCode: string,
  clientBirthDate: string | Date,
) {
  const state = zipCodeToState(clientZipCode);
  const clientAge = calculateAge(new Date(clientBirthDate));
  return (
    carescoutAvailableStates.includes(state.stateAbbreviation as any) &&
    clientAge <= MAX_ELIGIBLE_AGE_CARESCOUT
  );
}

/**
 * Guards a payment option restored from saved advisor defaults or a query
 * param. Those values travel across clients, and 'Short-Term Care' is only
 * offered to STC-eligible ones — restoring it elsewhere would select an option
 * the dropdown never renders, leaving a blank selector with no way back.
 */
export function clampPaymentOptionToEligibility<T extends string | undefined>(
  providedPaymentOption: T,
  isShortTermCareEligible: boolean,
): T | undefined {
  if (providedPaymentOption === SHORT_TERM_CARE_PAYMENT_OPTION && !isShortTermCareEligible) {
    return undefined;
  }
  return providedPaymentOption;
}

export function getShowSinglePayAnd10Pay(
  hideSinglePayAnd10PayFlag: boolean | undefined,
  clientBirthDate: string | Date,
) {
  const clientAge = calculateAge(new Date(clientBirthDate));
  return !hideSinglePayAnd10PayFlag && clientAge >= MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY;
}

export function getHideSinglePayAnd10Pay(
  hideSinglePayAnd10PayFlag: boolean | undefined,
  clientBirthDate: string | Date,
) {
  const clientAge = calculateAge(new Date(clientBirthDate));
  return !!hideSinglePayAnd10PayFlag || clientAge < MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY;
}
